<template>
  <div>
    <scorm-content
        :show="showTraining"
        :training="trainingLocation"
        :client-active="clientActive"
        :external-close-command="externalCloseCommand"
        :quiz-recorded="quizRecorded"
        :api-data="apiData"
        :status="apiStatus"
        :learner="learner"
        @post-message="postMessageToClient"
        @send-handshake="awaitHandshake"
        @post-to-api="postToAPI"
        @post-quiz-results="postQuizResults"
        @set-schema="setSchema"
    />
    <loading :show-loading="loading"/>
  </div>
</template>

<script>

import axios from "axios";
import ScormContent from "@/components/scormContent";
import Loading from "@/components/loading";

export default {
  name: "LandingPage",
  components: {
    Loading,
    ScormContent
  },
  props: {
    // Public directory to look in
    environment: {
      type: String,
      required: false,
      default: 'stage'
    },
    // Next level directory to look in
    customer: {
      type: String,
      required: false,
      default: 'flx'
    },
    // Specific training directory to use
    trainingName: {
      type: String,
      required: false,
      default: 'flx-workplace'
    },
    // To send tracking data to API
    productCertificationId: {
      type: Number,
      required: false,
      default: 0
    },
    // To send tracking data to API
    trainingId: {
      type: Number,
      required: false,
      default: 0
    },
    // To send tracking data to API
    scormId: {
      type: Number,
      required: false,
      default: 0
    },
    // To send tracking data to API
    employeeId: {
      type: Number,
      required: false,
      default: 0
    },
    // CMS/API url to send results
    baseUrl: {
      type: String,
      required: false,
      default: 'http://localhost:3000/'
    }
  },

  data: () => ({
    showTraining: false,
    trainingLocation: '',
    apiData: null,
    learner: null,
    schema: null,
    apiStatus: null,
    axiosClient: null,
    externalCloseCommand: false,
    loading: false,
    clientTimeout: null,
    clientActive: false, // determine if connected to a client app (analytics required)
    quizRecorded: false
  }),

  watch: {
    /**
     * @description
     * This watcher is active when the handshake to the client is complete. If successful, it then starts the
     * training in monitored mode by connecting the progress to the FLX API
     * @param {Boolean} val - True means connected to client
     */
    clientActive(val) {
      console.log('client active switch', val)
      if (val) {
        if (this.clientTimeout) {
          clearTimeout(this.clientTimeout)
        }
        this.loading = false
        this.getFromAPI()
      }
    }
  },

  created() {
  },

  beforeMount() {
    console.log('before mount landing page')
    this.trainingLocation = `${this.$props.environment}/${this.$props.customer}/${this.$props.trainingName}`
    this.axiosClient = axios.create({
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*"
      }
    })
  },

  mounted() {
    window.addEventListener('message', this.messageListener)
  },

  beforeDestroy() {
    window.removeEventListener('message', this.messageListener)
    this.axiosClient = null
  },

  methods: {

    /**
     * @description
     * Event listener to receive communication from the FLX Client
     * @param message
     */
    messageListener(message) {
      if (message.data.source === 'flx-client') {
        console.log('message listener', message.data.message)
        if (message.data.message === 'handshake') { // Client connection successful
          this.clientActive = true
        }
        if (message.data.message === 'close') { // Close signal from FLX Client
          // Tell scorm to close
          this.externalCloseCommand = true
        }
      }
    },

    async getFromAPI() {
      this.loading = true
      console.log('in getFromAPI')
      // Default params mean there is not enough information to connect this session to the API for recording.
      if ((this.productCertificationId === 0 && this.trainingId === 0) || this.scormId === 0 || this.employeeId === 0) {
        console.log('API Connection Failed')
        this.clientActive = false
        this.showTraining = true
        this.loading = false
        return
      }

      // Get scorm progress from API
      const data = {
        employee_id: this.employeeId,
        scorm_id: this.scormId,
        product_certification_id: this.productCertificationId,
        product_id: this.trainingId
      }
      const response = await this.axiosClient({
        method: 'POST',
        url: '/api/get_scorm',
        baseURL: this.baseUrl,
        data: {scorm: data}
      }).then(response => response.data)
          .catch(error => {
            console.log('error connecting to api:', error.response)
          })
      // console.log('response', response)

      // set local scorm values to what's saved on the api
      if (response?.training?.progress_string) {
        const progress = response.training.progress_string
        console.log('progress', JSON.parse(progress))
        this.apiData = JSON.parse(progress)
      } else {
        this.apiData = {}
        console.log('no results found')
      }

      if (response?.learner) {
        this.learner = response.learner
      }

      // Set local status from the response
      if (response?.training?.status) {
        this.apiStatus = response.training.status
      }
      this.loading = false
      this.showTraining = true
    },

    async postToAPI(status, progress = {}, flag = '') {
      console.log('in postToAPI')
      // console.log('in postToAPI', JSON.parse(JSON.stringify(progress)))
      if (!this.clientActive) {
        return
      }
      const data = {
        employee_id: this.employeeId,
        product_certification_id: this.productCertificationId,
        product_id: this.trainingId,
        scorm_id: this.scormId,
        progress: JSON.stringify(progress),
        schema: this.schema || '',
        status: status
      }
      await this.axiosClient({
        method: 'POST',
        url: '/api/update_scorm',
        baseURL: this.baseUrl,
        data: {scorm: data},
      });
      switch (flag) {
        case 'close':
          this.clientActive = false
          this.postMessageToClient('close')
          break
        case 'completed':
          this.markScormComplete()
          break
      }
    },

    postQuizResults(quizResults) {
      if (!this.clientActive) {
        this.quizRecorded = true
        return
      }
      this.axiosClient({
        method: 'POST',
        url: '/api/scorm_quiz_results',
        baseURL: this.$props.baseUrl,
        data: {
          quiz_results: quizResults,
          employee_id: this.$props.employeeId,
          scorm_id: this.$props.scormId
        }
      }).catch(error => {
        console.log('error recording quiz results', error)
      }).finally(() => this.quizRecorded = true)
    },

    setSchema(schemaValue) {
      this.schema = schemaValue
    },

    /**
     * @description
     * Sends a message to the FLX Client to start the handshake process. Then starts a timer that opens the
     * training in preview mode unless the handshake is complete.
     */
    awaitHandshake() {
      // console.log('awaiting handshake')
      this.postMessageToClient('get handshake')
      this.loading = true
      this.clientTimeout = setTimeout(() => {
        console.log('Starting in preview mode')
        this.loading = false
        this.showTraining = true
      }, 2500)
    },

    markScormComplete() {
      if (this.apiStatus !== 'completed') {
        this.apiStatus = 'completed'
        this.postMessageToClient('training-complete')
      }
    },

    postMessageToClient(message) {
      console.log('post message to client', message)
      window.parent.postMessage({
        'source': 'flx-scorm-player',
        'message': message
      }, '*')
    },
  }
}
</script>

<style scoped>

</style>